import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import CharacterCard from "@/components/CharacterCard";
import api from "@/lib/api";
import { Swords, Heart, Skull, Search } from "lucide-react";

const TYPES = [
  { v: "", l: "All personae" },
  { v: "rpg", l: "Tabletop RPG" },
  { v: "novel", l: "Novel" },
  { v: "screenplay", l: "Screenplay" },
  { v: "video_game", l: "Video Game" },
  { v: "comic", l: "Comic" },
  { v: "other", l: "Other" },
];

export default function Vault() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [roleplayOnly, setRoleplayOnly] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("top");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (type) params.set("character_type", type);
    if (roleplayOnly) params.set("license_tier", "roleplay");
    if (q.trim()) params.set("q", q.trim());
    params.set("sort", sort);
    const t = setTimeout(() => {
      api.get(`/characters?${params.toString()}`)
        .then(({ data }) => setItems(data))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [type, roleplayOnly, q, sort]);

  return (
    <div className="min-h-screen grain">
      <Nav />
      <section className="relative overflow-hidden border-b border-[rgba(217,180,114,0.1)]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 relative">
          <div className="chip mb-6" data-testid="vault-chip"><Swords className="w-3 h-3" /> Character Vault</div>
          <h1 className="font-serif-display text-4xl sm:text-6xl text-[#F7F3EC] tracking-tight mb-4 max-w-3xl">
            Forgotten heroes and villains, <span className="italic text-[#E6B860]">for hire.</span>
          </h1>
          <p className="font-serif-body text-lg text-[#A8A095] max-w-2xl leading-relaxed">
            Adopt a character no one else will finish. Each one comes with a full backstory, a stat block or a free-form build, and <span className="text-[#D9B472] italic">infinite battles and romance</span> ahead. Some carry a roleplay licence so you can drop them into your own campaign or novel.
          </p>

          <div className="mt-10 space-y-3">
            <div className="relative max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E675E]" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search names, hooks, tags, systems, pen names…"
                className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm pl-10 pr-4 py-2.5 text-[#F7F3EC] font-mono-brass text-sm"
                data-testid="vault-search"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap gap-1 border border-[rgba(217,180,114,0.15)] rounded-sm p-1" data-testid="vault-type-filter">
                {TYPES.map((t) => (
                  <button
                    key={t.v || "all"}
                    onClick={() => setType(t.v)}
                    className={`px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-mono-brass transition-colors duration-200 ${
                      type === t.v ? "bg-[#D9B472] text-[#0D0C0A]" : "text-[#A8A095] hover:text-[#F7F3EC]"
                    }`}
                    data-testid={`vault-type-${t.v || "all"}`}
                  >
                    {t.l}
                  </button>
                ))}
              </div>
              <div className="flex gap-1 border border-[rgba(217,180,114,0.15)] rounded-sm p-1" data-testid="vault-sort">
                {[["top","Top rated"], ["new","Newest"]].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setSort(v)}
                    className={`px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-mono-brass ${sort === v ? "bg-[#D9B472] text-[#0D0C0A]" : "text-[#A8A095] hover:text-[#F7F3EC]"}`}
                    data-testid={`vault-sort-${v}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A8A095] font-mono-brass cursor-pointer">
                <input type="checkbox" checked={roleplayOnly} onChange={(e) => setRoleplayOnly(e.target.checked)} className="accent-[#D9B472]" data-testid="vault-roleplay-toggle" />
                <Heart className="w-3 h-3 text-[#8C3A34]" /> Roleplay license only
              </label>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        {loading ? (
          <p className="text-[#6E675E] font-serif-body italic">Summoning the roster…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="vault-empty">
            <Skull className="w-14 h-14 text-[#D9B472] opacity-30 mx-auto mb-4" />
            <p className="text-[#A8A095] font-serif-body italic text-lg mb-3">No characters match those filters yet.</p>
            <Link to="/dashboard" className="text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest">Bring your own → Author Dashboard</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8" data-testid="vault-grid">
            {items.map((c, i) => <CharacterCard key={c.id} character={c} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
